import { useEffect } from 'react';
import {
  SITE_NAME,
  SITE_URL,
  DEFAULT_OG_IMAGE,
  TWITTER_HANDLE,
} from '@/config/seo';

const SCHEMA_ATTR = 'data-schema-id';

/**
 * SchemaOrg — injects a JSON-LD <script> tag into <head> and cleans up on
 * unmount or prop change. Each instance is keyed by a unique `id` so multiple
 * schemas can coexist without clobbering each other (and without colliding
 * with the `seo-jsonld` tag managed by useSEO).
 *
 * Returns null — render it anywhere in the tree.
 *
 * @param {Object} props
 * @param {Object|Array} props.data JSON-LD object or array of objects
 * @param {string} props.id         Unique key, e.g. "home-service"
 */
const SchemaOrg = ({ data, id }) => {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!data || !id) return undefined;

    const selector = `script[${SCHEMA_ATTR}="${id}"]`;
    let script = document.querySelector(selector);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(SCHEMA_ATTR, id);
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);

    return () => {
      const current = document.querySelector(selector);
      if (current) current.remove();
    };
  }, [data, id]);

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Schema builders — return plain JS objects ready to feed into <SchemaOrg />.
// ─────────────────────────────────────────────────────────────────────────────

const twitterHandleToUrl = (handle) => {
  if (!handle) return null;
  const trimmed = handle.replace(/^@/, '');
  return `https://twitter.com/${trimmed}`;
};

/**
 * Organization schema — use on homepage and canonicalize other schemas to
 * this via `@id`.
 */
export const buildOrganizationSchema = ({
  name = SITE_NAME,
  url = `${SITE_URL}/`,
  logo = `${SITE_URL}/favicon.png`,
  sameAs,
  description,
  address,
  foundingDate,
  contactPoint,
} = {}) => {
  const twitterUrl = twitterHandleToUrl(TWITTER_HANDLE);
  const defaultSameAs = twitterUrl ? [twitterUrl] : [];
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name,
    url,
    logo: {
      '@type': 'ImageObject',
      url: logo,
    },
    sameAs: sameAs && sameAs.length ? sameAs : defaultSameAs,
  };
  if (description) schema.description = description;
  if (address) schema.address = address;
  if (foundingDate) schema.foundingDate = foundingDate;
  if (contactPoint) schema.contactPoint = contactPoint;
  return schema;
};

/**
 * ProfessionalService schema — for the homepage service listing or a
 * dedicated service page. Provider defaults to the Organization `@id`.
 */
export const buildProfessionalServiceSchema = ({
  name = SITE_NAME,
  url = `${SITE_URL}/`,
  serviceType,
  areaServed = 'Worldwide',
  description,
  provider,
  image = DEFAULT_OG_IMAGE,
  priceRange = '$$',
  idSuffix = 'service',
} = {}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${SITE_URL}/#${idSuffix}`,
    name,
    url,
    image,
    priceRange,
    areaServed,
    provider: provider || { '@id': `${SITE_URL}/#organization` },
  };
  if (description) schema.description = description;
  if (serviceType) schema.serviceType = serviceType;
  return schema;
};

/**
 * Review schema — for client testimonials. `itemReviewed` defaults to the
 * Organization `@id` so reviews attach to the brand entity.
 */
export const buildReviewSchema = ({
  author,
  reviewBody,
  itemReviewed,
  reviewRating,
  datePublished,
  publisher,
} = {}) => {
  if (!author || !reviewBody) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    reviewBody,
    author: {
      '@type': typeof author === 'string' ? 'Person' : author['@type'] || 'Person',
      name: typeof author === 'string' ? author : author.name,
      ...(typeof author === 'object' && author.url ? { url: author.url } : {}),
    },
    itemReviewed: itemReviewed || { '@id': `${SITE_URL}/#organization` },
  };
  if (reviewRating) {
    schema.reviewRating = {
      '@type': 'Rating',
      ratingValue: reviewRating.ratingValue || 5,
      bestRating: reviewRating.bestRating || 5,
    };
  }
  if (datePublished) schema.datePublished = datePublished;
  if (publisher) schema.publisher = publisher;
  return schema;
};

export default SchemaOrg;
